import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface GlobalToast {
    id: number
    severity: 'error' | 'warn' | 'info' | 'success'
    summary: string
    detail?: string
}

export interface ErrorNotification {
    id: number
    summary: string
    detail?: string
    createdAt: number
}

let toastCounter = 0
let errorCounter = 0

export const useUiStore = defineStore('ui', () => {
    const showConcurrencyDialog = ref(false)
    const concurrencyMessage = ref('')
    const isVersionConflict = ref(false)
    const onRefresh = ref<(() => Promise<void>) | null>(null)
    const toasts = ref<GlobalToast[]>([])
    const errors = ref<ErrorNotification[]>([])

    function showToast(severity: GlobalToast['severity'], summary: string, detail?: string, durationMs = 8000) {
        const id = ++toastCounter
        toasts.value.push({ id, severity, summary, detail })
        setTimeout(() => {
            toasts.value = toasts.value.filter(t => t.id !== id)
        }, durationMs)

        if (severity === 'error') {
            sendNotification(summary, detail)
            errors.value.push({ id: ++errorCounter, summary, detail, createdAt: Date.now() })
        }
    }

    function dismissError(id: number) {
        errors.value = errors.value.filter(e => e.id !== id)
    }

    function clearErrors() {
        errors.value = []
    }

    function sendNotification(title: string, body?: string) {
        if (!('Notification' in window)) return
        if (Notification.permission === 'granted') {
            new Notification(title, { body, icon: '/favicon.ico' })
        } else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then(p => {
                if (p === 'granted') new Notification(title, { body, icon: '/favicon.ico' })
            })
        }
    }

    function dismissToast(id: number) {
        toasts.value = toasts.value.filter(t => t.id !== id)
    }

    function triggerConcurrencyError(message?: string, versionConflict: boolean = false, refreshFn?: () => Promise<void>) {
        concurrencyMessage.value = message || 'Destructive edits are not allowed while other members are active on this page.'
        isVersionConflict.value = versionConflict
        onRefresh.value = refreshFn || null
        showConcurrencyDialog.value = true
    }

    async function handleRefresh() {
        if (onRefresh.value) {
            await onRefresh.value()
        }
        showConcurrencyDialog.value = false
    }

    return {
        showConcurrencyDialog,
        concurrencyMessage,
        isVersionConflict,
        onRefresh,
        triggerConcurrencyError,
        handleRefresh,
        toasts,
        showToast,
        dismissToast,
        errors,
        dismissError,
        clearErrors
    }
})
